import React from "react";
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Paper,
  Grid,
  Divider,
  Alert,
  Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import LockIcon from "@mui/icons-material/Lock";
import "./EditRuleComponent.css";

const EditRuleComponent = ({
  resetEnabled,
  carryForwardEnabled,
  resetConfig,
  handleResetConfigChange,
  handleResetFrequencyChange,
  handleResetDayChange,
  handleResetDateSelect,
  carryForwardConfig,
  handleCarryForwardConfigChange,
  handleCarryForwardFrequencyChange,
  handleCarryForwardDayChange,
  handleCarryForwardDateSelect,
  loading,
  onUpdate,
  onCancel,
  existingRuleData,
}) => {
  // Days of week
  const weekDays = [
    { value: 1, label: "Mon" },
    { value: 2, label: "Tue" },
    { value: 3, label: "Wed" },
    { value: 4, label: "Thu" },
    { value: 5, label: "Fri" },
    { value: 6, label: "Sat" },
    { value: 7, label: "Sun" },
  ];

  // Frequency options
  const frequencyOptions = [
    { value: "DAILY", label: "Daily" },
    { value: "WEEKLY", label: "Weekly" },
    { value: "MONTHLY", label: "Monthly" },
  ];

  // Generate calendar days for monthly selection
  const generateCalendarDays = () => {
    const days = [];
    for (let i = 1; i <= 31; i++) {
      days.push(i);
    }
    return days;
  };

  const calendarDays = generateCalendarDays();

  // Check if at least one rule type is enabled
  const hasRules = resetEnabled || carryForwardEnabled;

  return (
    <Box className="edit-rule-container">
      <Paper elevation={0} className="rule-paper">
        <Box className="rule-header">
          <Typography variant="h5" className="rule-title">
            Edit Department Rules Configuration
          </Typography>
          <Typography variant="body2" className="rule-subtitle">
            Configure reset and carry forward rules for this department.
          </Typography>
        </Box>

        {/* Current Status Alert */}
        {existingRuleData && (
          <Alert severity="info" className="status-alert">
            <InfoIcon />
            <Typography variant="body2">
              Editing rules for Department ID: {existingRuleData.departmentId || "N/A"}
            </Typography>
          </Alert>
        )}

        {/* Rule Type Selection - DISABLED */}
        <Box className="type-selection">
          <Typography variant="subtitle1" className="section-title">
            Configured Rule Types
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card
                className={`type-card ${resetEnabled ? 'selected' : 'disabled'}`}
              >
                <CardContent className="type-card-content">
                  <Box className="type-card-header">
                    <Checkbox
                      checked={resetEnabled}
                      disabled
                      color="primary"
                      className="type-checkbox"
                      icon={<LockIcon />}
                      checkedIcon={<CheckCircleIcon />}
                    />
                    <Box className="type-title-container">
                      <Typography variant="subtitle1" className="type-title">
                        Reset Type
                      </Typography>
                      <Typography variant="caption" className="type-subtitle">
                        Balance Reset
                      </Typography>
                    </Box>
                    {resetEnabled && (
                      <Chip 
                        label="Configured" 
                        size="small" 
                        color="success" 
                        variant="outlined"
                      />
                    )}
                  </Box>
                  <Typography variant="body2" className="type-description">
                    Automatically reset user balances to a predefined amount at the start of each period.
                  </Typography>
                  {!resetEnabled && (
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      No reset rules configured for this department
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                className={`type-card ${carryForwardEnabled ? 'selected' : 'disabled'}`}
              >
                <CardContent className="type-card-content">
                  <Box className="type-card-header">
                    <Checkbox
                      checked={carryForwardEnabled}
                      disabled
                      color="primary"
                      className="type-checkbox"
                      icon={<LockIcon />}
                      checkedIcon={<CheckCircleIcon />}
                    />
                    <Box className="type-title-container">
                      <Typography variant="subtitle1" className="type-title">
                        Carry Forward Type
                      </Typography>
                      <Typography variant="caption" className="type-subtitle">
                        Balance Rollover
                      </Typography>
                    </Box>
                    {carryForwardEnabled && (
                      <Chip 
                        label="Configured" 
                        size="small" 
                        color="success" 
                        variant="outlined"
                      />
                    )}
                  </Box>
                  <Typography variant="body2" className="type-description">
                    Allow users to roll over a portion or all of their unused balance to the next period.
                  </Typography>
                  {!carryForwardEnabled && (
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      No carry forward rules configured for this department
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Selected Types Indicator */}
          {hasRules && (
            <Box className="selected-types">
              <Typography variant="body2" className="selected-label">
                Active Rule Types:
              </Typography>
              {resetEnabled && (
                <Box className="selected-chip">
                  <CheckCircleIcon className="chip-icon" />
                  <span>Reset</span>
                </Box>
              )}
              {carryForwardEnabled && (
                <Box className="selected-chip">
                  <CheckCircleIcon className="chip-icon" />
                  <span>Carry Forward</span>
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Divider className="section-divider" />

        {/* Configuration Sections - Only show if rule type is enabled */}
        <Box className="config-sections">
          {/* Reset Configuration - Only show if resetEnabled */}
          {resetEnabled && (
            <Box className="config-section reset-section">
              <Typography variant="h6" className="config-title">
                Reset Configuration
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box className="form-group">
                    <Typography variant="subtitle2" className="form-label">
                      Recharge Amount (₹)
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      value={resetConfig.amount}
                      onChange={(e) => handleResetConfigChange('amount', e.target.value)}
                      InputProps={{
                        inputProps: { min: 0, step: 0.01 },
                      }}
                      size="small"
                      placeholder="Enter amount"
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box className="form-group">
                    <Typography variant="subtitle2" className="form-label">
                      Frequency
                    </Typography>
                    <Box className="frequency-buttons">
                      {frequencyOptions.map((option) => (
                        <Button
                          key={option.value}
                          variant={resetConfig.frequency === option.value ? "contained" : "outlined"}
                          onClick={() => handleResetFrequencyChange(option.value)}
                          className={`frequency-btn ${resetConfig.frequency === option.value ? 'selected' : ''}`}
                          size="small"
                        >
                          {option.label}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                </Grid>

                {/* Day/Date Selection - Only show if not DAILY */}
                {resetConfig.frequency === "WEEKLY" && (
                  <Grid item xs={12}>
                    <Box className="form-group">
                      <Typography variant="subtitle2" className="form-label">
                        Select Day
                      </Typography>
                      <Box className="day-buttons">
                        {weekDays.map((day) => (
                          <Button
                            key={day.value}
                            variant={resetConfig.day === day.value ? "contained" : "outlined"}
                            onClick={() => handleResetDayChange(day.value)}
                            className={`day-btn ${resetConfig.day === day.value ? 'selected' : ''}`}
                            size="small"
                          >
                            {day.label}
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                )}

                {resetConfig.frequency === "MONTHLY" && (
                  <Grid item xs={12}>
                    <Box className="form-group">
                      <Typography variant="subtitle2" className="form-label">
                        Select Date (1-31)
                      </Typography>
                      <Box className="calendar-container">
                        <Grid container spacing={0.5}>
                          {calendarDays.map((day) => (
                            <Grid item xs={2.4} key={day}>
                              <Button
                                variant={resetConfig.day === day ? "contained" : "outlined"}
                                onClick={() => handleResetDateSelect(day)}
                                className={`calendar-btn ${resetConfig.day === day ? 'selected' : ''}`}
                                size="small"
                              >
                                {day}
                              </Button>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {/* Carry Forward Configuration - Only show if carryForwardEnabled */}
          {carryForwardEnabled && (
            <Box className="config-section carryforward-section">
              <Typography variant="h6" className="config-title">
                Carry Forward Configuration
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box className="form-group">
                    <Typography variant="subtitle2" className="form-label">
                      Recharge Amount (₹)
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      value={carryForwardConfig.amount}
                      onChange={(e) => handleCarryForwardConfigChange('amount', e.target.value)}
                      InputProps={{
                        inputProps: { min: 0, step: 0.01 },
                      }}
                      size="small"
                      placeholder="Enter amount"
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box className="form-group">
                    <Typography variant="subtitle2" className="form-label">
                      Low Balance Limit (₹)
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      value={carryForwardConfig.lowBalance}
                      onChange={(e) => handleCarryForwardConfigChange('lowBalance', e.target.value)}
                      InputProps={{
                        inputProps: { min: 0, step: 0.01 },
                      }}
                      helperText="Minimum balance for carry forward"
                      size="small"
                      placeholder="0"
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Box className="form-group">
                    <Typography variant="subtitle2" className="form-label">
                      Frequency
                    </Typography>
                    <Box className="frequency-buttons">
                      {frequencyOptions.map((option) => (
                        <Button
                          key={option.value}
                          variant={carryForwardConfig.frequency === option.value ? "contained" : "outlined"}
                          onClick={() => handleCarryForwardFrequencyChange(option.value)}
                          className={`frequency-btn ${carryForwardConfig.frequency === option.value ? 'selected' : ''}`}
                          size="small"
                        >
                          {option.label}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                </Grid>

                {/* Day/Date Selection - Only show if not DAILY */}
                {carryForwardConfig.frequency === "WEEKLY" && (
                  <Grid item xs={12}>
                    <Box className="form-group">
                      <Typography variant="subtitle2" className="form-label">
                        Select Day
                      </Typography>
                      <Box className="day-buttons">
                        {weekDays.map((day) => (
                          <Button
                            key={day.value}
                            variant={carryForwardConfig.day === day.value ? "contained" : "outlined"}
                            onClick={() => handleCarryForwardDayChange(day.value)}
                            className={`day-btn ${carryForwardConfig.day === day.value ? 'selected' : ''}`}
                            size="small"
                          >
                            {day.label}
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                )}

                {carryForwardConfig.frequency === "MONTHLY" && (
                  <Grid item xs={12}>
                    <Box className="form-group">
                      <Typography variant="subtitle2" className="form-label">
                        Select Date (1-31)
                      </Typography>
                      <Box className="calendar-container">
                        <Grid container spacing={0.5}>
                          {calendarDays.map((day) => (
                            <Grid item xs={2.4} key={day}>
                              <Button
                                variant={carryForwardConfig.day === day ? "contained" : "outlined"}
                                onClick={() => handleCarryForwardDateSelect(day)}
                                className={`calendar-btn ${carryForwardConfig.day === day ? 'selected' : ''}`}
                                size="small"
                              >
                                {day}
                              </Button>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </Box>

        {/* Action Buttons */}
        <Box className="edit-action-buttons">
          <Button
            onClick={onCancel}
            variant="outlined"
            className="cancel-btn"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={onUpdate}
            variant="contained"
            className="update-btn"
            disabled={loading || !hasRules}
          >
            {loading ? (
              <>
                <CircularProgress size={20} color="inherit" />
                <span style={{ marginLeft: '8px' }}>Updating...</span>
              </>
            ) : (
              'Save Rules'
            )}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default EditRuleComponent;